import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Loading from '../common/Loading';
import Api from '../common/Api.js';
import jwt_encode from 'jwt-encode';
import jwt_decode from 'jwt-decode';
import Footer from '../common/Footer';
import Header from '../common/Header';
import CustomerAuth from '../common/CustomerAuth';
import { omit } from 'lodash';
import Select from 'react-select';

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 38, height: 38 }),
  valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', overflow: 'hidden' }),
  singleValue: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

function Profile() {
  const [pageloading, setpageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({});
  const [errors, setErrors] = useState({});

  // State + City
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loadingCities, setLoadingCities] = useState(false);

  // ---------------------------------------------------------------------------
  // Load profile + states, preselect state + cities
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchdata = async () => {
      setpageLoading(true);
      const customer = JSON.parse(localStorage.getItem('login-token'));

      try {
        // 1. Profile
        const res = await Api.post(
          'customers/profile',
          jwt_encode(customer, process.env.REACT_APP_SECRET_KEY)
        );
        const result = jwt_decode(res.data);
        if (result.data.status === 'failure') {
          toast.error(result.data.errors.msg, { position: 'bottom-right', autoClose: 5000 });
          setpageLoading(false);
          return;
        }
        const prof = result.data.data || {};
        setProfile(prof);

        // 2. States
        const sRes = await Api.get('/home/states');
        const stateList = (sRes.data || []).map((s) => ({
          value: s.value,
          label: s.label,
        }));
        setStates(stateList);

        // 3. Preselect state — by code first, fall back to name
        let stateObj = null;
        if (prof.state_code) {
          stateObj = stateList.find(
            (s) => String(s.value).toUpperCase() === String(prof.state_code).toUpperCase()
          );
        }
        if (!stateObj && prof.state) {
          stateObj = stateList.find(
            (s) => String(s.label).toUpperCase() === String(prof.state).toUpperCase()
          );
        }
        setSelectedState(stateObj || null);

        // 4. Cities for that state
        if (stateObj) {
          setLoadingCities(true);
          try {
            const { data: cityRows } = await Api.get('/home/cities', {
              params: { code: stateObj.value },
            });
            const cityList = (cityRows || []).map((c) => ({
              value: c.name ?? c.value,
              label: c.name ?? c.label,
            }));
            setCities(cityList);

            if (prof.city) {
              const cityObj = cityList.find(
                (c) => String(c.value).toUpperCase() === String(prof.city).toUpperCase()
              );
              setSelectedCity(cityObj || { value: prof.city, label: prof.city });
            }
          } finally {
            setLoadingCities(false);
          }
        }

        setpageLoading(false);
      } catch (err) {
        setpageLoading(false);
        toast.error('Failed to load profile.', { position: 'bottom-right', autoClose: 5000 });
      }
    };

    fetchdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Inputs
  // ---------------------------------------------------------------------------
  const handleChange = (event) => {
    event.persist();
    const { name, value } = event.target;
    changeValidate(event, name, value);
    setProfile({ ...profile, [name]: value });
  };

  const handleState = async (selected) => {
    setSelectedState(selected);
    setSelectedCity(null);
    setCities([]);

    setProfile((prev) => ({
      ...prev,
      state_code: selected?.value || '',
      state: selected?.label || '',
      city: '',          // reset city until picked
    }));

    if (!selected) return;

    setLoadingCities(true);
    try {
      const { data } = await Api.get('/home/cities', {
        params: { code: selected.value },
      });
      setCities((data || []).map((c) => ({ value: c.name, label: c.name })));
    } catch (err) {
      toast.error('Failed to load cities', { position: 'bottom-right', autoClose: 5000 });
    } finally {
      setLoadingCities(false);
    }
  };

  const handleCity = (selected) => {
    setSelectedCity(selected);
    setProfile((prev) => ({ ...prev, city: selected?.value || '' }));
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const payload = {
      ...profile,
      state_code: selectedState?.value || profile.state_code || '',
      state:      selectedState?.label || profile.state || '',
      city:       selectedCity?.value || profile.city || '',
    };

    try {
      const res = await Api.post(
        'customers/update',
        jwt_encode(payload, process.env.REACT_APP_SECRET_KEY)
      );
      const result = jwt_decode(res.data);
      setLoading(false);

      if (result.data.status === 'failure') {
        setErrors(result.data.errors);
      } else {
        localStorage.setItem('login-token', JSON.stringify(result.data.data));
        setErrors({});
        toast.success('Your profile updated successfully !!!', {
          position: 'bottom-right', autoClose: 5000,
        });
      }
    } catch (err) {
      setLoading(false);
      toast.error('Something went wrong. Please try again.');
    }
  };

  // ---------------------------------------------------------------------------
  // Live field validation (unchanged from your version)
  // ---------------------------------------------------------------------------
  const changeValidate = (event, name, value) => {
    switch (name) {
      case 'firstName':
        if (!value.match(/^[a-zA-Z]+$/) && value !== '') {
          setErrors({ ...errors, firstName: 'you can not use numbers,symbols & spaces in first name.' });
        } else {
          setErrors(omit(errors, 'firstName'));
        }
        break;
      case 'middleName':
        if (!value.match(/^[a-zA-Z]+$/) && value !== '') {
          setErrors({ ...errors, middleName: 'you can not use numbers,symbols & spaces in middle name.' });
        } else {
          setErrors(omit(errors, 'middleName'));
        }
        break;
      case 'lastName':
        if (!value.match(/^[a-zA-Z]+$/) && value !== '') {
          setErrors({ ...errors, lastName: 'you can not use numbers,symbols & spaces in last name.' });
        } else {
          setErrors(omit(errors, 'lastName'));
        }
        break;
      case 'age':
        if (!/^[0-9]+$/.test(value)) {
          setErrors({ ...errors, age: 'age should be numbers.' });
        } else if (value.trim().length !== 2) {
          setErrors({ ...errors, age: 'age should be exact 2 numbers.' });
        } else {
          setErrors(omit(errors, 'age'));
        }
        break;
      case 'aadhar_no':
        if (!/^[0-9]+$/.test(value)) {
          setErrors({ ...errors, aadhar_no: 'aadhar no should be numbers.' });
        } else if (value.trim().length !== 12) {
          setErrors({ ...errors, aadhar_no: 'aadhar no should be exact 12 numbers.' });
        } else {
          setErrors(omit(errors, 'aadhar_no'));
        }
        break;
      case 'pan_no':
        if (!/^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/.test(value)) {
          setErrors({ ...errors, pan_no: 'please enter valid pan card number.' });
        } else {
          setErrors(omit(errors, 'pan_no'));
        }
        break;
      case 'pincode':
        if (!/^[0-9]+$/.test(value)) {
          setErrors({ ...errors, pincode: 'pincode should be numbers.' });
        } else if (value.trim().length !== 6) {
          setErrors({ ...errors, pincode: 'pincode should be exact 6 numbers.' });
        } else {
          setErrors(omit(errors, 'pincode'));
        }
        break;
      default:
        break;
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <CustomerAuth />
      <Header />
      <section className="w3l-faq-main">
        <div className="faq-sec sec-padding">
          <div className="wrapper">
            <div className="row">
              <div className="col-md-12">
                <div className="card card-primary">
                  <div className="card-header">
                    <h3 className="card-title">My Profile</h3>
                  </div>

                  {pageloading ? (
                    <Loading />
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="card-body">
                        <div className="row">

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>First Name</label>
                              <input
                                type="text"
                                name="firstName"
                                value={profile.firstName || ''}
                                onChange={handleChange}
                                className={errors.firstName ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter First Name"
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.firstName && (
                                <span className="error invalid-feedback">{errors.firstName}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Middle Name</label>
                              <input
                                type="text"
                                name="middleName"
                                value={profile.middleName || ''}
                                onChange={handleChange}
                                className={errors.middleName ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Middle Name"
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.middleName && (
                                <span className="error invalid-feedback">{errors.middleName}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Last Name</label>
                              <input
                                type="text"
                                name="lastName"
                                value={profile.lastName || ''}
                                onChange={handleChange}
                                className={errors.lastName ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Last Name"
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.lastName && (
                                <span className="error invalid-feedback">{errors.lastName}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Email</label>
                              <input
                                type="text"
                                name="email"
                                value={profile.email || ''}
                                onChange={handleChange}
                                className={errors.email ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Email"
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.email && (
                                <span className="error invalid-feedback">{errors.email}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Gender</label>
                              <select
                                name="gender"
                                value={profile.gender || ''}
                                onChange={handleChange}
                                className={errors.gender ? 'form-control is-invalid' : 'form-control'}
                                style={{ padding: '.375rem .75rem' }}
                              >
                                <option value="">--select gender--</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                              </select>
                              {errors.gender && (
                                <span className="error invalid-feedback">{errors.gender}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Age</label>
                              <input
                                type="text"
                                name="age"
                                maxLength={2}
                                value={profile.age || ''}
                                onChange={handleChange}
                                className={errors.age ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Age"
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.age && (
                                <span className="error invalid-feedback">{errors.age}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Aadhar No</label>
                              <input
                                type="text"
                                name="aadhar_no"
                                value={profile.aadhar_no || ''}
                                onChange={handleChange}
                                className={errors.aadhar_no ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Aadhar No."
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.aadhar_no && (
                                <span className="error invalid-feedback">{errors.aadhar_no}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Pan No</label>
                              <input
                                type="text"
                                name="pan_no"
                                value={profile.pan_no || ''}
                                onChange={handleChange}
                                className={errors.pan_no ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Pan No."
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.pan_no && (
                                <span className="error invalid-feedback">{errors.pan_no}</span>
                              )}
                            </div>
                          </div>

                          {/* State — react-select */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>State</label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                styles={selectStyles}
                                name="state"
                                value={selectedState}
                                onChange={handleState}
                                options={states}
                                placeholder="Select State"
                                isClearable
                                isSearchable
                              />
                              {errors.state && (
                                <span className="error invalid-feedback d-block">{errors.state}</span>
                              )}
                            </div>
                          </div>

                          {/* City — react-select, depends on state */}
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>City</label>
                              <Select
                                className="basic-single"
                                classNamePrefix="select"
                                styles={selectStyles}
                                name="city"
                                value={selectedCity}
                                onChange={handleCity}
                                options={cities}
                                isLoading={loadingCities}
                                isDisabled={!selectedState}
                                placeholder={selectedState ? 'Select City' : 'Select state first'}
                                isClearable
                                isSearchable
                              />
                              {errors.city && (
                                <span className="error invalid-feedback d-block">{errors.city}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Pincode</label>
                              <input
                                type="text"
                                name="pincode"
                                value={profile.pincode || ''}
                                onChange={handleChange}
                                className={errors.pincode ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Pincode"
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.pincode && (
                                <span className="error invalid-feedback">{errors.pincode}</span>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Residence Address</label>
                              <textarea
                                name="address"
                                value={profile.address || ''}
                                onChange={handleChange}
                                className={errors.address ? 'form-control is-invalid' : 'form-control'}
                                placeholder="Enter Residence Address"
                                style={{ padding: '.375rem .75rem' }}
                              />
                              {errors.address && (
                                <span className="error invalid-feedback">{errors.address}</span>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>

                      <div className="card-footer">
                        <button type="submit" className="btn button-eff" disabled={loading}>
                          <span style={{ marginLeft: '35px' }}>Submit</span>
                          <span style={{ float: 'right', width: '10px', height: '10px', marginLeft: '5px' }}>
                            {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                          </span>
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Profile;